const chapter = {
  id: 'units-measurements',
  slug: 'units-measurements',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Units, Dimensions & Measurement',
        hindi: 'मात्रक, विमा और मापन',
  },
  estReadMinutes: 40,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '1 question in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 1 प्रश्न',
  },
  syllabusTags: ['Physics-Basics', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'units',
      title: {
        english: 'SI Units & Fundamental Quantities',
        hindi: 'SI मात्रक और मूल राशियाँ',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'A **physical quantity** is anything that can be measured. The **SI** (Système International) system defines **seven fundamental quantities**, each with one base unit. All other quantities are **derived**.',
        hindi: '**भौतिक मात्रा** वह चीज़ है जिसे मापा जा सकता है। **एसआई** (सिस्टम इंटरनेशनल) प्रणाली **सात मूलभूत मात्राओं** को परिभाषित करती है, प्रत्येक एक आधार इकाई के साथ। अन्य सभी मात्राएँ **व्युत्पन्न** हैं।',
          },
        },
        {
          type: 'table',
          headers: ['Quantity', 'Symbol', 'SI Unit', 'Symbol'],
          rows: [
            ['Length', '$L$', 'metre', 'm'],
            ['Mass', '$M$', 'kilogram', 'kg'],
            ['Time', '$T$', 'second', 's'],
            ['Electric current', '$I$ (or $A$)', 'ampere', 'A'],
            ['Temperature', '$\\Theta$ (or $K$)', 'kelvin', 'K'],
            ['Amount of substance', '$N$', 'mole', 'mol'],
            ['Luminous intensity', '$J$', 'candela', 'cd'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Supplementary units**: radian (plane angle), steradian (solid angle).',
        hindi: '**पूरक इकाइयाँ**: रेडियन (समतल कोण), स्टेरेडियन (ठोस कोण)।',
            },
            {
              english: '**Common SI prefixes**: pico ($10^{-12}$), nano ($10^{-9}$), micro ($10^{-6}$), milli ($10^{-3}$), kilo ($10^{3}$), mega ($10^{6}$), giga ($10^{9}$), tera ($10^{12}$).',
        hindi: '**सामान्य एसआई उपसर्ग**: पिको ($10^{-12}$), नैनो ($10^{-9}$), माइक्रो ($10^{-6}$), मिलि ($10^{-3}$), किलो ($10^{3}$), मेगा ($10^{6}$), गीगा ($10^{9}$), टेरा ($10^{12}$)।',
            },
            {
              english: '**Astronomical**: $1$ light-year $= 9.46 \\times 10^{15}$ m. $1$ AU $= 1.496 \\times 10^{11}$ m. $1$ parsec $= 3.08 \\times 10^{16}$ m.',
        hindi: '**खगोलीय**: $1$ प्रकाश-वर्ष $= 9.46 \\times 10^{15}$ मी. $1$ एयू $= 1.496 \\times 10^{11}$ मी. $1$ पारसेक $= 3.08 \\times 10^{16}$ मी.',
            },
          ],
        },
      ],
    },
    {
      id: 'dimensions',
      title: {
        english: 'Dimensional Formulae',
        hindi: 'विमीय सूत्र',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Dimensions',
        hindi: 'DIMENSIONS',
          },
          body: {
            english: 'The **dimensions** of a physical quantity are the powers to which the fundamental quantities must be raised to represent it. Written as $[M^a L^b T^c \\ldots]$.',
        hindi: 'किसी भौतिक मात्रा के **आयाम** वे शक्तियाँ हैं, जिनका प्रतिनिधित्व करने के लिए मूलभूत मात्राओं को ऊपर उठाया जाना चाहिए। $[M^a L^b T^c \\ldots]$ के रूप में लिखा गया।',
          },
        },
        {
          type: 'table',
          headers: ['Quantity', 'Formula', 'Dimensional formula'],
          rows: [
            ['Velocity', '$L/T$', '$[M^0 L^1 T^{-1}]$'],
            ['Acceleration', '$L/T^2$', '$[M^0 L^1 T^{-2}]$'],
            ['Force', '$ma$', '$[M^1 L^1 T^{-2}]$'],
            ['Work / Energy', '$F \\cdot d$', '$[M^1 L^2 T^{-2}]$'],
            ['Power', '$W/t$', '$[M^1 L^2 T^{-3}]$'],
            ['Pressure', '$F/A$', '$[M^1 L^{-1} T^{-2}]$'],
            ['Momentum', '$mv$', '$[M^1 L^1 T^{-1}]$'],
            ['Impulse', '$F \\cdot t$', '$[M^1 L^1 T^{-1}]$'],
            ['Frequency', '$1/T$', '$[M^0 L^0 T^{-1}]$'],
            ['Angular velocity', '$\\theta/t$', '$[M^0 L^0 T^{-1}]$'],
            ['Torque', '$r \\times F$', '$[M^1 L^2 T^{-2}]$'],
            ['Stress', '$F/A$', '$[M^1 L^{-1} T^{-2}]$'],
            ['Strain', 'dimensionless', '$[M^0 L^0 T^0]$'],
            ['Young\'s modulus', '$\\text{stress}/\\text{strain}$', '$[M^1 L^{-1} T^{-2}]$'],
            ['Surface tension', '$F/L$', '$[M^1 L^0 T^{-2}]$'],
            ['Coefficient of viscosity', '$\\eta$', '$[M^1 L^{-1} T^{-1}]$'],
            ['Planck\'s constant', '$E/\\nu$', '$[M^1 L^2 T^{-1}]$'],
            ['Gravitational constant $G$', '$Fr^2/m^2$', '$[M^{-1} L^3 T^{-2}]$'],
          ],
        },
      ],
    },
    {
      id: 'analysis',
      title: {
        english: 'Uses & Limitations of Dimensional Analysis',
        hindi: 'विमीय विश्लेषण के उपयोग और सीमाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Checking correctness** — both sides of an equation must have the same dimensions (**Principle of homogeneity**).',
        hindi: '**सटीकता की जाँच** - समीकरण के दोनों पक्षों के आयाम समान होने चाहिए (**एकरूपता का सिद्धांत**)।',
            },
            {
              english: '**Conversion of units** — $n_1 u_1 = n_2 u_2$, with $n_2 = n_1 \\left(\\dfrac{M_1}{M_2}\\right)^a \\left(\\dfrac{L_1}{L_2}\\right)^b \\left(\\dfrac{T_1}{T_2}\\right)^c$.',
        hindi: '**इकाइयों का रूपांतरण** — $n_1 u_1 = n_2 u_2$, $n_2 = n_1 \\left(\\dfrac{M_1}{M_2}\\right)^a \\left(\\dfrac{L_1}{L_2}\\right)^b \\left(\\dfrac{T_1}{T_2}\\right)^c$ के साथ।',
            },
            {
              english: '**Deriving relations** — when a quantity depends on a few others, dimensional analysis fixes the form up to a numerical constant.',
        hindi: '**संबंध व्युत्पन्न** - जब एक मात्रा कुछ अन्य पर निर्भर करती है, तो आयामी विश्लेषण एक संख्यात्मक स्थिरांक तक रूप को ठीक करता है।',
            },
          ],
        },
        {
          type: 'warning',
          text: {
            english: 'Limitations: cannot determine **dimensionless constants**, **trigonometric/exponential/logarithmic** functions, or distinguish between quantities with the same dimensions (e.g. work and torque). Fails when more unknown powers than equations.',
        hindi: 'सीमाएँ: **आयाम रहित स्थिरांक**, **trigonometric/exponential/लघुगणक** फ़ंक्शन निर्धारित नहीं कर सकते, या समान आयाम वाली मात्राओं के बीच अंतर नहीं कर सकते (उदाहरण के लिए कार्य और टॉर्क)। समीकरणों से अधिक अज्ञात घात होने पर विफल हो जाता है।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'The time period $T$ of a simple pendulum may depend on its length $\\ell$, mass $m$, and gravitational acceleration $g$. Derive the formula for $T$.',
        hindi: 'एक साधारण लोलक की समयावधि $T$ उसकी लंबाई $\\ell$, द्रव्यमान $m$ और गुरुत्वाकर्षण त्वरण $g$ पर निर्भर हो सकती है। $T$ के लिए सूत्र प्राप्त करें।',
          },
          solution: {
            english: 'Assume $T = k\\,\\ell^a m^b g^c$. Dimensions: $[T] = [L]^a [M]^b [LT^{-2}]^c$. Comparing: $L: a + c = 0$, $M: b = 0$, $T: -2c = 1 \\Rightarrow c = -1/2, a = 1/2$. So $T = k\\sqrt{\\ell/g}$. (Constant $k = 2\\pi$ from theory.)',
        hindi: 'मान लीजिए $T = k\\,\\ell^a m^b g^c$. आयाम: $[T] = [L]^a [M]^b [LT^{-2}]^c$. तुलना: $L: a + c = 0$, $M: b = 0$, $T: -2c = 1 \\Rightarrow c = -1/2, a = 1/2$। तो $T = k\\sqrt{\\ell/g}$. (सिद्धांत से स्थिरांक $k = 2\\pi$।)',
          },
        },
      ],
    },
    {
      id: 'errors',
      title: {
        english: 'Errors in Measurement',
        hindi: 'मापन में त्रुटियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Systematic errors** — same direction every time (instrumental, environmental, personal). Reduced by calibration.',
        hindi: '**व्यवस्थित त्रुटियाँ** - हर बार एक ही दिशा (वाद्य, पर्यावरणीय, व्यक्तिगत)। अंशांकन द्वारा कम किया गया.',
            },
            {
              english: '**Random errors** — fluctuate; reduced by averaging multiple readings.',
        hindi: '**यादृच्छिक त्रुटियाँ** — उतार-चढ़ाव; एकाधिक रीडिंग के औसत से कम किया गया।',
            },
            {
              english: '**Gross errors** — outright human mistakes.',
        hindi: '**घोर गलतियाँ** - पूर्णतया मानवीय गलतियाँ।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Mean, absolute & relative errors',
        hindi: 'माध्य, निरपेक्ष और सापेक्ष त्रुटियाँ',
          },
          body: {
            english: `For $n$ readings $a_1, a_2, \\ldots, a_n$:
• Mean: $\\bar a = \\dfrac{1}{n}\\sum a_i$.
• Absolute error in $i$-th reading: $\\Delta a_i = |\\bar a - a_i|$.
• Mean absolute error: $\\overline{\\Delta a} = \\dfrac{1}{n}\\sum \\Delta a_i$.
• Relative (fractional) error: $\\dfrac{\\overline{\\Delta a}}{\\bar a}$.
• Percentage error: $\\dfrac{\\overline{\\Delta a}}{\\bar a} \\times 100\\%$.`,
            hindi: '$n$ रीडिंग के लिए $a_1, a_2, \\ldots, a_n$: • माध्य: $\\bar a = \\dfrac{1}{n}\\sum a_i$. • $i$वीं रीडिंग में पूर्ण त्रुटि: $\\Delta a_i = |\\bar a - a_i|$। • माध्य पूर्ण त्रुटि: $\\overline{\\Delta a} = \\dfrac{1}{n}\\sum \\Delta a_i$। • सापेक्ष (आंशिक) त्रुटि: $\\dfrac{\\overline{\\Delta a}}{\\bar a}$. • प्रतिशत त्रुटि: $\\dfrac{\\overline{\\Delta a}}{\\bar a} \\times 100\\\\\\\\%$.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Propagation of errors',
        hindi: 'त्रुटियों का प्रसार',
          },
          statement: {
            english: `For $Z = A^p B^q / C^r$:
$$\\dfrac{\\Delta Z}{Z} = p\\,\\dfrac{\\Delta A}{A} + q\\,\\dfrac{\\Delta B}{B} + r\\,\\dfrac{\\Delta C}{C}.$$
For $Z = A \\pm B$: $\\Delta Z = \\Delta A + \\Delta B$ (absolute errors add).
For $Z = AB$ or $A/B$: relative errors add.`,
            hindi: '$Z = A^p B^q / C^r$ के लिए: $$\\dfrac{\\Delta Z}{Z} = p\\,\\dfrac{\\Delta A}{A} + q\\,\\dfrac{\\Delta B}{B} + r\\,\\dfrac{\\Delta C}{C}.$$ $Z = A \\pm B$ के लिए: $\\Delta Z = \\Delta A + \\Delta B$ (पूर्ण त्रुटियाँ जोड़ें)। $Z = AB$ या $A/B$ के लिए: सापेक्ष त्रुटियाँ जोड़ें।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Density $\\rho = m/V$. If $\\Delta m / m = 1\\%$ and $\\Delta V / V = 2\\%$, what is the maximum percent error in $\\rho$?',
        hindi: 'घनत्व $\\rho = m/V$. यदि $\\Delta m / m = 1\\\\%$ और $\\Delta V / V = 2\\\\%$, तो $\\rho$ में अधिकतम प्रतिशत त्रुटि क्या है?',
          },
          solution: {
            english: '$\\dfrac{\\Delta \\rho}{\\rho} = \\dfrac{\\Delta m}{m} + \\dfrac{\\Delta V}{V} = 1\\% + 2\\% = 3\\%$.',
        hindi: '$\\dfrac{\\Delta \\rho}{\\rho} = \\dfrac{\\Delta m}{m} + \\dfrac{\\Delta V}{V} = 1\\% + 2\\% = 3\\%$.',
          },
        },
      ],
    },
    {
      id: 'sigfigs',
      title: {
        english: 'Significant Figures & Rounding',
        hindi: 'सार्थक अंक और पूर्णांकन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'All non-zero digits are significant.',
        hindi: 'सभी गैर-शून्य अंक महत्वपूर्ण हैं।',
            },
            {
              english: 'Zeros between non-zero digits are significant.',
        hindi: 'गैर-शून्य अंकों के बीच शून्य महत्वपूर्ण हैं।',
            },
            {
              english: 'Leading zeros are NOT significant.',
        hindi: 'अग्रणी शून्य महत्वपूर्ण नहीं हैं।',
            },
            {
              english: 'Trailing zeros after a decimal point ARE significant.',
        hindi: 'दशमलव बिंदु के बाद अनुगामी शून्य महत्वपूर्ण होते हैं।',
            },
            {
              english: 'In **addition/subtraction**, the result has the same number of decimal places as the term with the fewest decimal places.',
        hindi: '**addition/subtraction** में, परिणाम में दशमलव स्थानों की संख्या सबसे कम दशमलव स्थानों वाले पद के समान होती है।',
            },
            {
              english: 'In **multiplication/division**, the result has the same number of significant figures as the factor with the fewest.',
        hindi: '**multiplication/division** में, परिणाम में महत्वपूर्ण अंकों की संख्या सबसे कम वाले कारक के समान होती है।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: '**Rounding rule**: if the digit after the last significant digit is 5, round to make the last digit even ("banker\'s rounding") in scientific contexts.',
        hindi: '**पूर्णांकन नियम**: यदि अंतिम महत्वपूर्ण अंक के बाद का अंक 5 है, तो वैज्ञानिक संदर्भों में अंतिम अंक को सम ("बैंकर का पूर्णांक") बनाने के लिए पूर्णांक बनाएं। **वर्नियर कैलिपर्स**: कम से कम । विशिष्ट .1 मिमी. **स्क्रू गेज/माइक्रोमीटर**: । विशिष्ट .01 मिमी.',
          },
        },
      ],
    },
    {
      id: 'instruments',
      title: {
        english: 'Vernier Callipers & Screw Gauge',
        hindi: 'वर्नियर कैलिपर्स और पेच गेज',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Vernier callipers**: least count = (1 main scale division) − (1 vernier scale division). Typical LC = 0.1 mm.',
        hindi: '**वर्नियर कैलिपर्स**: न्यूनतम गिनती = (1 मुख्य स्केल डिवीजन) - (1 वर्नियर स्केल डिवीजन)। विशिष्ट एलसी = 0.1 मिमी।',
            },
            {
              english: '**Screw gauge / micrometer**: LC = pitch / (number of divisions on circular scale). Typical LC = 0.01 mm.',
        hindi: '**स्क्रू गेज / माइक्रोमीटर**: एलसी = पिच / (गोलाकार पैमाने पर डिवीजनों की संख्या)। विशिष्ट एलसी = 0.01 मिमी।',
            },
            {
              english: '**Zero error**: positive (zero of vernier/circular scale to the right of main scale zero) or negative (to the left). Always **subtract** zero error from observed reading.',
        hindi: '**शून्य त्रुटि**: सकारात्मक (मुख्य स्केल शून्य के दाईं ओर vernier/circular स्केल का शून्य) या नकारात्मक (बाईं ओर)। हमेशा देखी गई रीडिंग में से शून्य त्रुटि को **घटाना**** करें।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '[F] = [MLT^{-2}]',
      desc: {
        english: 'Force',
        hindi: 'बल',
      },
    },
    {
      latex: '[E] = [ML^2T^{-2}]',
      desc: {
        english: 'Energy',
        hindi: 'ऊर्जा',
      },
    },
    {
      latex: '[P] = [ML^{-1}T^{-2}]',
      desc: {
        english: 'Pressure',
        hindi: 'दबाव',
      },
    },
    {
      latex: 'n_2 = n_1 (M_1/M_2)^a (L_1/L_2)^b (T_1/T_2)^c',
      desc: {
        english: 'Unit conversion',
        hindi: 'इकाई रूपांतरण',
      },
    },
    {
      latex: '\\Delta Z / Z = p\\Delta A/A + q\\Delta B/B',
      desc: {
        english: 'Error propagation',
        hindi: 'त्रुटि प्रसार',
      },
    },
    {
      latex: 'T = 2\\pi\\sqrt{\\ell/g}',
      desc: {
        english: 'Pendulum (dimensional)',
        hindi: 'पेंडुलम (आयामी)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Dimensions',
      problem: {
        english: 'Dimensional formula of $\\dfrac{1}{2}\\epsilon_0 E^2$ (energy density of electric field).',
        hindi: '$\\dfrac{1}{2}\\epsilon_0 E^2$ (विद्युत क्षेत्र की ऊर्जा घनत्व) का आयामी सूत्र।',
      },
      answer: {
        english: '$[ML^{-1}T^{-2}]$',
        hindi: '$[ML^{-1}T^{-2}]$',
      },
      solution: {
        english: 'Same as pressure; energy / volume.',
        hindi: 'दबाव के समान; ऊर्जा/आयतन.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Errors',
      problem: {
        english: 'In an experiment $g = 4\\pi^2 L/T^2$. If $\\Delta L / L = 0.5\\%$ and $\\Delta T / T = 1\\%$, find max error in $g$.',
        hindi: 'एक प्रयोग $g = 4\\pi^2 L/T^2$ में। यदि $\\Delta L / L = 0.5\\\\%$ और $\\Delta T / T = 1\\\\%$, तो $g$ में अधिकतम त्रुटि ज्ञात करें।',
      },
      answer: {
        english: '$2.5\\%$',
        hindi: '$2.5\\\\%$',
      },
      solution: {
        english: '$\\Delta g / g = \\Delta L/L + 2 \\Delta T/T = 0.5 + 2 = 2.5\\%$.',
        hindi: '$\\Delta g / g = \\Delta L/L + 2 \\Delta T/T = 0.5 + 2 = 2.5\\%$.',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Sig figs',
      problem: {
        english: 'How many significant figures are in $0.0080360$?',
        hindi: '$0.0080360$ में कितने सार्थक अंक हैं?',
      },
      answer: {
        english: '5',
        hindi: '5',
      },
      solution: {
        english: 'Leading zeros not significant; trailing zero after decimal is significant.',
        hindi: 'अग्रणी शून्य महत्वपूर्ण नहीं हैं; दशमलव के बाद शून्य का आना महत्वपूर्ण है।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Vernier',
      problem: {
        english: 'In a vernier with 20 vernier divisions = 19 main scale divisions (1 MSD = 1 mm), find the least count.',
        hindi: '20 वर्नियर divisions = 19 main scale divisions (1 MSD = 1 mm) वाले वर्नियर में, न्यूनतम गिनती ज्ञात करें।',
      },
      answer: {
        english: '0.05 mm',
        hindi: '0.05 मिमी',
      },
      solution: {
        english: '1 VSD = 19/20 mm. LC = 1 − 19/20 = 1/20 mm = 0.05 mm.',
        hindi: '1 वीएसडी = 19/20 मिमी. एलसी = 1 − 19/20 = 1/20 मिमी = 0.05 मिमी।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Dimensional derivation',
      problem: {
        english: 'A physical quantity depends on $G, h, c$ as $[\\text{quantity}] = G^a h^b c^d$. Find dimensions of $\\sqrt{Gh/c^3}$ (Planck length).',
        hindi: 'एक भौतिक मात्रा $G, h, c$ पर $[\\text{quantity}] = G^a h^b c^d$ के रूप में निर्भर करती है। $\\sqrt{Gh/c^3}$ (प्लैंक लंबाई) के आयाम खोजें।',
      },
      answer: {
        english: '$[L]$',
        hindi: '$[L]$',
      },
      solution: {
        english: '$[G] = [M^{-1}L^3T^{-2}], [h] = [ML^2T^{-1}], [c] = [LT^{-1}]$. Compute $[Gh/c^3] = [M^{-1}L^3T^{-2}][ML^2T^{-1}][L^{-3}T^3] = [L^2]$. Square root → $[L]$.',
        hindi: '$[जी] = [एम^{-1}एल^3टी^{-2}], [एच] = [एमएल^2टी^{-1}], [सी] = [एलटी^{-1}]$. $[Gh/c^3] = [M^{-1}L^3T^{-2}][ML^2T^{-1}][L^{-3}T^3] = [L^2]$ की गणना करें। वर्गमूल → $[L]$.',
      },
    },
  ],
  next: {
    slug: 'kinematics',
    title: 'Kinematics',
  },
};

export default chapter;
